import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Select } from "components/select/index";
import { useFormBuilderEditorContext } from "../../../formBuilderEditorContext";
import { IFormBuilderRow, TFormBuilderRowAlignItems, TFormBuilderRowJustifyContent, TFormBuilderRowSettings } from "../../../model/formBuilderRow";

interface IFormBuilderRowSettingsProps {
  row: IFormBuilderRow;
}

interface IFormBuilderRowSettingsOption<T> {
  label: string;
  value: T;
}

export function FormBuilderRowSettings(props: IFormBuilderRowSettingsProps) {
  const { row } = props;

  const { t } = useTranslation();

  const { formBuilderController } = useFormBuilderEditorContext();

  const settings = row.data.settings;

  const justifyContentOptions: IFormBuilderRowSettingsOption<TFormBuilderRowJustifyContent>[] = useMemo(() => {
    return [
      { label: t("Start"), value: "flex-start" },
      { label: t("Center"), value: "center" },
      { label: t("End"), value: "flex-end" },
      { label: t("Space Between"), value: "space-between" },
    ];
  }, []);

  const alignItemsOptions: IFormBuilderRowSettingsOption<TFormBuilderRowAlignItems>[] = useMemo(() => {
    return [
      { label: t("Start"), value: "flex-start" },
      { label: t("Center"), value: "center" },
      { label: t("End"), value: "flex-end" },
    ];
  }, []);

  function dispatchSettings(partialNextSettings: Partial<TFormBuilderRowSettings>) {
    formBuilderController.updateRowSettings({
      key: row.key,
      prevRowSettings: cloneDeep(settings),
      nextRowSettings: {
        ...cloneDeep(settings),
        ...partialNextSettings,
      },
    });
  }

  function renderOptions<T extends string | number>(options: IFormBuilderRowSettingsOption<T>[]) {
    const { Option } = Select;

    return (
      <>
        {options.map((option, index) => (
          <Option key={index} value={option.value}>
            <span>{option.label}</span>
          </Option>
        ))}
      </>
    );
  }

  return (
    <>
      <Select
        label={t("Justify")}
        defaultValue={settings.justifyContent}
        onChange={(justifyContent: TFormBuilderRowJustifyContent) => {
          if (justifyContent !== settings.justifyContent) {
            dispatchSettings({
              justifyContent,
            });
          }
        }}
      >
        {renderOptions(justifyContentOptions)}
      </Select>

      <Select
        block
        label={t("Align")}
        defaultValue={settings.alignItems}
        onChange={(alignItems: TFormBuilderRowAlignItems) => {
          if (alignItems !== settings.alignItems) {
            dispatchSettings({
              alignItems,
            });
          }
        }}
      >
        {renderOptions(alignItemsOptions)}
      </Select>
    </>
  );
}
